import { parseToRgba, rgba } from 'color2k';
import { ALPHA_THRESHOLD } from '../constants';
import type { HSL } from '../types';

export const blend = (fgWithAlpha: string, solidBg: string): string => {
  const [fgR, fgG, fgB, fgA] = parseToRgba(fgWithAlpha);
  const [bgR, bgG, bgB] = parseToRgba(solidBg);

  const alpha = 1 - fgA;
  const red = Math.round((fgA * (fgR / 255) + alpha * (bgR / 255)) * 255);
  const green = Math.round((fgA * (fgG / 255) + alpha * (bgG / 255)) * 255);
  const blue = Math.round((fgA * (fgB / 255) + alpha * (bgB / 255)) * 255);

  return rgba(red, green, blue, 1);
};

export const isWhiteColor = (
  { lightness, alpha }: HSL,
  alphaThreshold = ALPHA_THRESHOLD
): boolean => {
  return (
    lightness === 100 || Boolean(alpha !== undefined && alpha <= alphaThreshold)
  );
};

/*
 * @description: Converts HSL color to string
 * @input: hue, saturation, lightness, alpha
 * @output: HSL string
 */
export const toHslString = (color?: HSL): string => {
  const h = color?.hue ?? 0;
  const s = color?.saturation ?? 0;
  const l = color?.lightness ?? 0;

  if (typeof color?.alpha === 'number') {
    return `hsla(${h}, ${s}%, ${l}%, ${color.alpha})`;
  }

  return `hsl(${h}, ${s}%, ${l}%)`;
};

/*
 * @description: Converts HSLA object to string
 * @input: hue, saturation, lightness, alpha?
 * @output: HSLA string
 */
export const toHslaString = (h = 0, s = 0, l = 0, a?: number): string => {
  if (typeof a === 'number') {
    return `hsla(${h}, ${s}%, ${l}%, ${a})`;
  }

  return `hsl(${h}, ${s}%, ${l}%)`;
};

/*
 * @description: Basic HEX validation
 * @input: string
 * @output: boolean
 */
export const isHexColor = (color: string): boolean => {
  return /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/.test(color);
};

/*
 * @description: Parse HEX string to HSL
 * @input: string, alpha
 * @output: HSL color
 */
export function hexToHsl(hex: string, alpha = 1): HSL {
  // Remove "#" if present
  hex = hex.replace(/^#/, '');

  // Handle short HEX (e.g., #f00)
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((char) => char + char)
      .join('');
  }

  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1));

    switch (max) {
      case r:
        h = ((g - b) / delta) % 6;
        break;
      case g:
        h = (b - r) / delta + 2;
        break;
      case b:
        h = (r - g) / delta + 4;
        break;
    }

    h *= 60;
    if (h < 0) h += 360;
  }

  return {
    hue: Math.round(h),
    saturation: parseInt((s * 100).toFixed(1)),
    lightness: parseInt((l * 100).toFixed(1)),
    alpha,
  };
}
